import {
  IconCheck,
  IconChevronRight,
  IconCopy,
  IconHeadset,
  IconMail,
  IconMessageCircle,
  IconPhone,
  IconX,
} from "@tabler/icons-react";
import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import useFocusTrap from "@/hooks/useFocusTrap";
import useScrollLock from "@/hooks/useScrollLock";
import {isChatwootConfigured, openLiveChat} from "@/services/chatwoot.service";
import {useAuthStore} from "@/store/useAuth.store";
import {useLogo} from "@/util/logoUtils";
import styles from "./SupportModal.module.css";

const MODAL_TITLE_ID = "support-modal-title";

const SupportModal = ({open, onClose}) => {
  const modalRef = useRef(null);
  const logo = useLogo();
  const [copied, setCopied] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(false);
  const isAuthenticated = useAuthStore(state => state.authStatus === "authenticated");
  useFocusTrap(modalRef, open);
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = e => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleOverlayClick = e => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("soporte@randrush.com");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenChat = async () => {
    if (chatLoading) return;
    setChatLoading(true);
    setChatError(false);
    try {
      await openLiveChat();
      onClose();
    } catch (_error) {
      setChatError(true);
    } finally {
      setChatLoading(false);
    }
  };

  return createPortal(
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={MODAL_TITLE_ID}
    >
      <div
        className={styles.modal}
        ref={modalRef}
      >
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Cerrar"
        >
          <IconX size={18} />
        </button>

        <div className={styles.header}>
          <img
            src={logo}
            alt="Randrush"
            className={styles.logo}
          />
          <h2
            className={styles.title}
            id={MODAL_TITLE_ID}
          >
            ¿Necesitas ayuda?
          </h2>
          <p className={styles.subtitle}>¿Alguna duda? Escríbenos por WhatsApp o correo. Estamos para ayudarte.</p>
        </div>

        <div className={styles.contactList}>
          <button
            type="button"
            onClick={handleCopyEmail}
            className={styles.contactRow}
          >
            <div className={`${styles.contactIcon} ${styles.iconEmail}`}>
              <IconMail size={18} />
            </div>
            <div className={styles.contactInfo}>
              <span className={styles.contactLabel}>Correo electrónico</span>
              <span className={styles.contactValue}>soporte@randrush.com</span>
            </div>
            {copied ? (
              <IconCheck
                size={16}
                className={styles.contactCopied}
              />
            ) : (
              <IconCopy
                size={16}
                className={styles.contactChevron}
              />
            )}
          </button>

          <a
            href="https://t.me/randrush_bot"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.contactRow}
          >
            <div className={`${styles.contactIcon} ${styles.iconTelegram}`}>
              <IconMessageCircle size={18} />
            </div>
            <div className={styles.contactInfo}>
              <span className={styles.contactLabel}>Telegram</span>
              <span className={styles.contactValue}>t.me/randrush_bot</span>
            </div>
            <IconChevronRight
              size={16}
              className={styles.contactChevron}
            />
          </a>

          {/* WhatsApp temporalmente deshabilitado: se conserva para reactivarlo luego. */}
          <div
            className={`${styles.contactRow} ${styles.contactRowDisabled}`}
            aria-disabled="true"
          >
            <div className={`${styles.contactIcon} ${styles.iconWhatsApp}`}>
              <IconPhone size={18} />
            </div>
            <div className={styles.contactInfo}>
              <span className={styles.contactLabel}>WhatsApp</span>
              <span className={styles.contactValue}>No disponible por el momento</span>
            </div>
          </div>

          {isAuthenticated && isChatwootConfigured && (
            <button
              type="button"
              onClick={handleOpenChat}
              disabled={chatLoading}
              className={styles.contactRow}
            >
              <div className={`${styles.contactIcon} ${styles.iconChat}`}>
                <IconHeadset size={18} />
              </div>
              <div className={styles.contactInfo}>
                <span className={styles.contactLabel}>Chat en vivo</span>
                <span className={styles.contactValue}>
                  {chatError ? "No se pudo abrir el chat. Toca para reintentar" : "Habla con un agente"}
                </span>
              </div>
              <IconChevronRight
                size={16}
                className={styles.contactChevron}
              />
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SupportModal;
